/**
 * Juwelier Reichart · Anprobe am Handgelenk.
 * Zeichnet Unterarm und Hand (Handrücken oben, Finger nach rechts, Daumen zum Betrachter) als SVG in Millimetern und legt
 * das freigestellte Uhrenfoto maßstabsgetreu darauf. Einstellbar: Handgelenkumfang, Statur, Hautton, Behaarung,
 * linker/rechter Arm. Die Wahl wird je Gerät gemerkt.
 * Daten kommen aus dem Metafeld reichart.anprobe (anprobe-hochladen.py) über data-anprobe am Element.
 */

const SVG = 'http://www.w3.org/2000/svg';
const BREITE = 300;
const MITTE_Y = 72;
const UHR_X = 100;
const KNOECHEL_X = 204;

const HAUTTOENE = ['#f3d5c0', '#e6bc98', '#cf9b74', '#b07650', '#8a5638', '#5c3a27'];
const STATUR = {
  schmal: { unterarm: 1.24, hand: 1.34 },
  normal: { unterarm: 1.36, hand: 1.4 },
  kraeftig: { unterarm: 1.5, hand: 1.46 },
};
const HAARE = { keine: 0, leicht: 70, mittel: 190, stark: 380 };
const SPEICHER = 'jr-anprobe';

/** @param {string} hex @param {number} faktor <1 dunkler, >1 heller */
function ton(hex, faktor) {
  const n = parseInt(hex.slice(1), 16);
  const kanal = (/** @type {number} */ v) => Math.max(0, Math.min(255, Math.round(faktor < 1 ? v * faktor : v + (255 - v) * (faktor - 1))));
  return `rgb(${kanal(n >> 16)} ${kanal((n >> 8) & 255)} ${kanal(n & 255)})`;
}

/** Glatte Kurve durch Punkte (Catmull-Rom → Bézier). @param {number[][]} p @param {boolean} [zu] geschlossen */
function kurve(p, zu = false) {
  const pt = zu ? [p[p.length - 1], ...p, p[0], p[1]] : [p[0], ...p, p[p.length - 1]];
  let d = `M${pt[1][0].toFixed(2)},${pt[1][1].toFixed(2)}`;
  for (let i = 1; i < pt.length - 2; i++) {
    const [p0, p1, p2, p3] = [pt[i - 1], pt[i], pt[i + 1], pt[i + 2]];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += `C${c1[0].toFixed(2)},${c1[1].toFixed(2)} ${c2[0].toFixed(2)},${c2[1].toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return zu ? `${d}Z` : d;
}

/** Kleiner Zufallsgenerator mit festem Startwert, damit die Haare beim Umstellen nicht springen. @param {number} s */
function zufall(s) {
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/** @param {string} name @param {Record<string, string|number>} [attr] */
function el(name, attr = {}) {
  const e = document.createElementNS(SVG, name);
  for (const [k, v] of Object.entries(attr)) e.setAttribute(k, String(v));
  return e;
}

/**
 * Ein Finger als leicht verjüngte Röhre mit runder Kuppe.
 * @param {number[]} basis Ansatz (Mitte) @param {number} winkel Grad, positiv = nach unten @param {number} laenge @param {number} breite
 */
function finger(basis, winkel, laenge, breite) {
  const w = (winkel * Math.PI) / 180;
  const u = [Math.cos(w), Math.sin(w)];
  const n = [-u[1], u[0]];
  const punkt = (/** @type {number} */ t, /** @type {number} */ s) => [basis[0] + u[0] * t + n[0] * s, basis[1] + u[1] * t + n[1] * s];
  const umriss = kurve(
    [punkt(0, breite / 2), punkt(laenge * 0.35, breite * 0.5), punkt(laenge * 0.7, breite * 0.45), punkt(laenge - breite * 0.4, breite * 0.42),
      punkt(laenge - breite * 0.08, breite * 0.24), punkt(laenge, 0), punkt(laenge - breite * 0.08, -breite * 0.24), punkt(laenge - breite * 0.4, -breite * 0.42),
      punkt(laenge * 0.7, -breite * 0.45), punkt(laenge * 0.35, -breite * 0.5), punkt(0, -breite / 2)],
    true,
  );
  const nagel = kurve([punkt(laenge - breite * 0.78, breite * 0.26), punkt(laenge - breite * 0.18, breite * 0.24), punkt(laenge - breite * 0.06, 0),
    punkt(laenge - breite * 0.18, -breite * 0.24), punkt(laenge - breite * 0.78, -breite * 0.26)], true);
  const falte = (/** @type {number} */ t, /** @type {number} */ s) => {
    const a = punkt(t, breite * s);
    const m = punkt(t + breite * 0.06, 0);
    const b = punkt(t, -breite * s);
    return `M${a[0].toFixed(2)},${a[1].toFixed(2)}Q${m[0].toFixed(2)},${m[1].toFixed(2)} ${b[0].toFixed(2)},${b[1].toFixed(2)}`;
  };
  return { umriss, nagel, falten: [falte(laenge * 0.5, 0.26), falte(laenge * 0.53, 0.18), falte(laenge * 0.76, 0.2)], knoechel: punkt(16, 0) };
}

class JrAnprobe extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bereit) return;
    this.dataset.bereit = '1';
    /** @type {{bild:string, mitte_x:number, mitte_y:number, gehaeuse_anteil:number, band_anteil:number, gehaeuse_mm:number|null, band_mm:number|null, seitenverhaeltnis:number}} */
    this.daten = JSON.parse(this.dataset.anprobe || '{}');
    const herren = this.dataset.typ === 'Herrenuhr';
    this.wahl = { umfang: herren ? 17.5 : 15.5, statur: herren ? 'normal' : 'schmal', haut: 1, haare: herren ? 'leicht' : 'keine', arm: 'links' };
    try {
      Object.assign(this.wahl, JSON.parse(localStorage.getItem(SPEICHER) || '{}'));
    } catch {}

    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    this.buehne = /** @type {SVGSVGElement} */ (this.querySelector('[data-buehne]'));
    this.angabe = this.querySelector('[data-angabe]');
    this.querySelector('[data-oeffnen]')?.addEventListener('click', () => {
      this.dialog?.showModal();
      this.zeichnen();
    });
    this.querySelector('[data-schliessen]')?.addEventListener('click', () => this.dialog?.close());
    // Direktaufruf von außen (z. B. Exponat auf der Startseite): …/products/x#anprobe öffnet die Anprobe gleich
    if (location.hash === '#anprobe') {
      requestAnimationFrame(() => {
        this.dialog?.showModal();
        this.zeichnen();
      });
    }
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });

    const regler = /** @type {HTMLInputElement|null} */ (this.querySelector('[name="umfang"]'));
    if (regler) {
      regler.value = String(this.wahl.umfang);
      regler.addEventListener('input', () => this.setzen('umfang', parseFloat(regler.value)));
    }
    for (const knopf of this.querySelectorAll('[data-wahl]')) {
      const [feld, wert] = /** @type {string} */ (knopf.getAttribute('data-wahl')).split(':');
      knopf.addEventListener('click', () => this.setzen(feld, feld === 'haut' ? Number(wert) : wert));
    }
    this.markieren();
  }

  /** @param {string} feld @param {string|number} wert */
  setzen(feld, wert) {
    /** @type {any} */ (this.wahl)[feld] = wert;
    try {
      localStorage.setItem(SPEICHER, JSON.stringify(this.wahl));
    } catch {}
    this.markieren();
    this.zeichnen();
  }

  markieren() {
    for (const knopf of this.querySelectorAll('[data-wahl]')) {
      const [feld, wert] = /** @type {string} */ (knopf.getAttribute('data-wahl')).split(':');
      knopf.setAttribute('aria-pressed', String(String(/** @type {any} */ (this.wahl)[feld]) === wert));
    }
    const ausgabe = this.querySelector('[data-umfang]');
    if (ausgabe) ausgabe.textContent = `${this.wahl.umfang.toFixed(1).replace('.', ',')} cm`;
  }

  zeichnen() {
    const svg = this.buehne;
    if (!svg) return;
    const d = this.daten;
    const st = STATUR[/** @type {keyof typeof STATUR} */ (this.wahl.statur)] || STATUR.normal;
    const haut = HAUTTOENE[this.wahl.haut] || HAUTTOENE[1];
    const rechts = this.wahl.arm === 'rechts';
    // Breite des Handgelenks von oben gesehen: ≈ 34,5 % des Umfangs (Ellipse mit Tiefe/Breite ≈ 0,7)
    const hg = this.wahl.umfang * 10 * 0.345;
    const ua = hg * st.unterarm;
    const hd = hg * st.hand;
    const y = MITTE_Y;
    const k = KNOECHEL_X;

    // Unterarm und Handrücken; oben Kleinfingerseite (mit Ellenköpfchen), unten Daumenseite.
    // Die Knöchelkante bekommt keine Kontur, damit die Finger nahtlos aus dem Handrücken wachsen.
    const obenKante = [[-10, y - ua / 2], [40, y - ua * 0.49], [84, y - hg * 0.58], [112, y - hg / 2], [121, y - hg / 2 - 1], [131, y - hg * 0.5],
      [160, y - hd * 0.47], [k - 6, y - hd * 0.475]];
    const vorne = [[k + 1, y - hd * 0.4], [k + 4, y - hd * 0.14], [k + 4, y + hd * 0.12], [k + 1, y + hd * 0.38]];
    const untenKante = [[k - 8, y + hd * 0.47], [176, y + hd * 0.48], [150, y + hg * 0.6], [128, y + hg * 0.52], [112, y + hg / 2], [84, y + hg * 0.58],
      [40, y + ua * 0.49], [-10, y + ua / 2]];
    const umriss = kurve([...obenKante, ...vorne, ...untenKante, [-14, y]], true);
    const kontur = `${kurve(obenKante)} ${kurve(untenKante)}`;

    // Finger dicht nebeneinander, Zeigefinger unten (Daumenseite) bis kleiner Finger oben; Ansatz unter dem Handrücken
    const fingerDaten = [
      { y: 0.345, w: 0.245, l: 0.9, a: 4 },
      { y: 0.1, w: 0.25, l: 0.99, a: 1 },
      { y: -0.145, w: 0.235, l: 0.93, a: -2.5 },
      { y: -0.36, w: 0.2, l: 0.74, a: -7 },
    ].map((f) => finger([k - 16, y + f.y * hd], f.a, f.l * hd + 12, f.w * hd));
    const daumen = finger([146, y + hg * 0.4], 30, hd * 0.64, hd * 0.31);

    svg.setAttribute('viewBox', `0 0 ${BREITE} 150`);
    svg.replaceChildren();
    const defs = el('defs');
    const verlauf = el('linearGradient', { id: 'jrHautVerlauf', gradientUnits: 'userSpaceOnUse', x1: 0, y1: y - ua / 2, x2: 0, y2: y + ua / 2 + 22 });
    [[0, ton(haut, 0.74)], [0.2, ton(haut, 0.96)], [0.42, ton(haut, 1.07)], [0.68, haut], [1, ton(haut, 0.7)]].forEach(([o, c]) => verlauf.append(el('stop', { offset: o, 'stop-color': c })));
    const wickel = el('linearGradient', { id: 'jrBandWickel', x1: 0, y1: 0, x2: 0, y2: 1 });
    [[0, 0.55], [0.12, 0.15], [0.3, 0], [0.7, 0], [0.88, 0.18], [1, 0.6]].forEach(([o, a]) => wickel.append(el('stop', { offset: o, 'stop-color': '#000', 'stop-opacity': a })));
    const schatten = el('filter', { id: 'jrUhrSchatten', x: -0.2, y: -0.2, width: 1.4, height: 1.4 });
    schatten.append(el('feDropShadow', { dx: 0.6, dy: 1.4, stdDeviation: 1.2, 'flood-color': '#000', 'flood-opacity': 0.35 }));
    const armSchatten = el('filter', { id: 'jrArmSchatten', x: -0.1, y: -0.2, width: 1.2, height: 1.5 });
    armSchatten.append(el('feDropShadow', { dx: 0, dy: 2.4, stdDeviation: 2.6, 'flood-color': '#3a2a1c', 'flood-opacity': 0.22 }));
    const clip = el('clipPath', { id: 'jrArmClip' });
    clip.append(el('path', { d: umriss, transform: rechts ? `translate(${BREITE} 0) scale(-1 1)` : '' }));
    const knoechel = el('radialGradient', { id: 'jrKnoechel' });
    [[0, 0.32], [1, 0]].forEach(([o, a]) => knoechel.append(el('stop', { offset: o, 'stop-color': ton(haut, 1.18), 'stop-opacity': a })));
    defs.append(verlauf, wickel, schatten, armSchatten, clip, knoechel);
    svg.append(defs);

    const kante = ton(haut, 0.6);
    const arm = el('g', { transform: rechts ? `translate(${BREITE} 0) scale(-1 1)` : '', filter: 'url(#jrArmSchatten)' });
    const teil = (/** @type {{umriss:string, nagel:string, falten:string[]}} */ f) => {
      const g = el('g');
      g.append(el('path', { d: f.umriss, fill: 'url(#jrHautVerlauf)', stroke: kante, 'stroke-width': 0.3 }));
      g.append(el('path', { d: f.nagel, fill: ton(haut, 1.22), stroke: ton(haut, 0.74), 'stroke-width': 0.22, opacity: 0.9 }));
      const falten = el('g', { fill: 'none', stroke: ton(haut, 0.7), 'stroke-width': 0.3, 'stroke-linecap': 'round', opacity: 0.5 });
      f.falten.forEach((p) => falten.append(el('path', { d: p })));
      g.append(falten);
      return g;
    };
    arm.append(teil(daumen));
    [...fingerDaten].reverse().forEach((f) => arm.append(teil(f)));
    arm.append(el('path', { d: umriss, fill: 'url(#jrHautVerlauf)' }));
    arm.append(el('path', { d: kontur, fill: 'none', stroke: kante, 'stroke-width': 0.3 }));

    // Knöchel als zarte Lichter, Sehnen vom Handgelenk zu den Knöcheln
    const relief = el('g', { fill: 'none', 'stroke-linecap': 'round' });
    fingerDaten.forEach((f) => {
      relief.append(el('path', { d: `M128,${y + (f.knoechel[1] - y) * 0.45} Q${(128 + f.knoechel[0]) / 2},${y + (f.knoechel[1] - y) * 0.82} ${f.knoechel[0] - 3},${f.knoechel[1]}`, stroke: ton(haut, 0.86), 'stroke-width': 0.6, opacity: 0.45 }));
      relief.append(el('ellipse', { cx: f.knoechel[0] - 2, cy: f.knoechel[1], rx: 4.5, ry: 5.5, fill: 'url(#jrKnoechel)', stroke: 'none' }));
    });
    arm.append(relief);

    const anzahl = HAARE[/** @type {keyof typeof HAARE} */ (this.wahl.haare)] || 0;
    if (anzahl) {
      const r = zufall(1864);
      const haare = el('g', { stroke: this.wahl.haut > 2 ? '#1c120c' : '#4a3426', 'stroke-width': 0.17, 'stroke-linecap': 'round', fill: 'none', opacity: 0.6 });
      const strich = (/** @type {number} */ x, /** @type {number} */ yy, /** @type {number} */ l, /** @type {number} */ w) =>
        haare.append(el('path', { d: `M${x.toFixed(1)},${yy.toFixed(1)} q${(l * 0.5).toFixed(1)},${(Math.sin(w) * l * 0.5 - 0.5).toFixed(1)} ${(Math.cos(w) * l).toFixed(1)},${(Math.sin(w) * l).toFixed(1)}` }));
      for (let i = 0; i < anzahl; i++) {
        const x = r() * 100 - 6;
        const breite = ua - (ua - hg) * Math.max(0, Math.min(1, x / 112));
        const yy = y + (r() - 0.5) * breite * 0.88;
        strich(x, yy, 2.6 + r() * 3.2, ((-8 + r() * 26) * Math.PI) / 180 + ((yy - y) / breite) * 0.9);
      }
      if (this.wahl.haare !== 'leicht') {
        const mehr = this.wahl.haare === 'stark' ? 60 : 24;
        for (let i = 0; i < mehr; i++) strich(140 + r() * 56, y + (r() - 0.5) * hd * 0.75, 1.8 + r() * 1.6, ((r() - 0.5) * 30 * Math.PI) / 180);
        if (this.wahl.haare === 'stark') {
          fingerDaten.forEach((f) => {
            for (let i = 0; i < 4; i++) strich(f.knoechel[0] + 6 + r() * 14, f.knoechel[1] + (r() - 0.5) * 5, 1.4 + r(), 0);
          });
        }
      }
      arm.append(haare);
    }
    svg.append(arm);

    // Uhr: Bildbreite in mm aus Gehäuse- oder Bandmaß, Gehäusemitte auf das Handgelenk
    const breite = d.gehaeuse_mm ? d.gehaeuse_mm / d.gehaeuse_anteil : (d.band_mm || 20) / d.band_anteil;
    const hoehe = breite * d.seitenverhaeltnis;
    const mitte = rechts ? BREITE - UHR_X : UHR_X;
    const uhr = el('g', { 'clip-path': 'url(#jrArmClip)' });
    uhr.append(el('image', { href: d.bild, x: (mitte - d.mitte_x * breite).toFixed(2), y: (y - d.mitte_y * hoehe).toFixed(2), width: breite.toFixed(2), height: hoehe.toFixed(2), filter: 'url(#jrUhrSchatten)', preserveAspectRatio: 'none' }));
    uhr.append(el('rect', { x: (mitte - (d.band_anteil * breite) / 2 - 1).toFixed(2), y: (y - hg * 0.64).toFixed(2), width: (d.band_anteil * breite + 2).toFixed(2), height: (hg * 1.28).toFixed(2), fill: 'url(#jrBandWickel)', style: 'mix-blend-mode:multiply' }));
    svg.append(uhr);

    if (this.angabe) {
      const mass = d.gehaeuse_mm ? `Gehäuse ${String(d.gehaeuse_mm).replace('.', ',')} mm` : `Band ${String(d.band_mm).replace('.', ',')} mm`;
      const anteil = d.gehaeuse_mm ? ` · bedeckt etwa ${Math.round((d.gehaeuse_mm / hg) * 100)} % der Handgelenkbreite` : '';
      this.angabe.textContent = `${mass} am Handgelenk mit ${this.wahl.umfang.toFixed(1).replace('.', ',')} cm Umfang (ca. ${Math.round(hg)} mm breit)${anteil}`;
    }
  }
}

if (!customElements.get('jr-anprobe')) customElements.define('jr-anprobe', JrAnprobe);
